import type { TabViewNavProps } from "./tab-view-nav.type";
import type { TabsProps } from "antd";

import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";

import { TAB_PARAM_KEYS } from "@/constants/TabKeys";

export const useTabViewNav = ({
  items = [],
  defaultActiveKey,
  onChange,
  tabKey = TAB_PARAM_KEYS.CONTEXT_TAB_KEY,
  paramsToKeep,
  alwaysSyncSearchParams = false,
  shouldSync,
  ...tabsProps
}: TabViewNavProps): TabsProps & { key: string } => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine active tab from URL
  const activeTab = searchParams.has(tabKey)
    ? items.find(
        (tab) =>
          String(tab.key).toLowerCase() ===
          searchParams.get(tabKey)?.toLowerCase()
      )
    : undefined;

  const activeTabKey: string =
    activeTab?.key ?? defaultActiveKey ?? items[0]?.key;

  // Set tab in URL
  const setTab = ({
    tabValue,
    replace = false,
  }: {
    tabValue: string;
    replace?: boolean;
  }) => {
    const newParams = new URLSearchParams();

    // Keep specified parameters
    if (paramsToKeep?.length) {
      for (const paramKey of paramsToKeep) {
        const paramValue = searchParams.get(paramKey);
        if (paramValue) newParams.set(paramKey, paramValue);
      }
    }

    // Set tab parameter
    newParams.set(tabKey, tabValue);

    // Update search parameters
    setSearchParams(newParams, {
      replace,
      state: { from: location.state?.from },
    });
  };

  // Sync URL with active tab if needed
  useEffect(() => {
    if (!alwaysSyncSearchParams) return;
    if (shouldSync && !shouldSync(searchParams)) return;

    const currentUrlTabValue = searchParams.get(tabKey);

    if (!currentUrlTabValue && activeTabKey) {
      setTab({ tabValue: activeTabKey, replace: true });
    }
  }, [alwaysSyncSearchParams, searchParams, activeTabKey, shouldSync, tabKey]);

  // Handle tab change
  const onTabChange = (key: string) => {
    const selectedTab = items.find((tab) => tab.key === key);
    if (selectedTab) {
      setTab({ tabValue: String(selectedTab.key).toLowerCase() });
    }

    onChange?.(key);
  };

  return {
    ...tabsProps,
    key: location.pathname,
    items,
    defaultActiveKey,
    activeKey: activeTabKey,
    onChange: onTabChange,
  };
};
